import { useState, useEffect } from "react";
import { useLanguage, API } from "../App";
import { motion } from "framer-motion";
import axios from "axios";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { ExternalLink, Sparkles, Check, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { DemoRequestModal } from "./DemoRequestModal";
import { trackPopupRedirect, trackCTAClick } from "../utils/analytics";

// Official GED Logos - Uniform height for all
const GEDLogos = {
  zeendoc: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_4f3dc9ba-8fc8-4fff-96ff-60223c7a8223/artifacts/cskigm18_2f90b336-437c-4703-9aaa-a858df680c7a.png" 
      alt="Zeendoc" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
  docuware: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_4f3dc9ba-8fc8-4fff-96ff-60223c7a8223/artifacts/yrn5at9h_logo%20docuware.GIF" 
      alt="DocuWare" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
  yooz: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_5577aad2-a1d8-4595-9afc-d525b5b1b883/artifacts/6og8kqyj_Yooz-Logo-Full-Colors-Transparent%20%283%29.png" 
      alt="Yooz" 
      className="h-[36px] max-w-[120px] object-contain"
    />
  ),
  holded: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_4f3dc9ba-8fc8-4fff-96ff-60223c7a8223/artifacts/y5gvxrsj_logo%20holded.png" 
      alt="Holded" 
      className="h-[40px] max-w-[130px] object-contain"
    />
  ),
  openkm: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/89huz0wr_logo%20openkm.jpg" 
      alt="OpenKM" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
  neoledge: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/pcnw3uxp_logo%20neoledge%201.0.GIF" 
      alt="NeoLedge" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
  twind: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/fjy58uyr_logo%20twind.GIF" 
      alt="Twind" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
  doxis: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/kc9bmomj_logo%20doxis.GIF" 
      alt="Doxis" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
  opentext: () => (
    <img 
      src="https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/v7iwc12m_open%20text%20logo%202.png" 
      alt="OpenText" 
      className="h-[45px] max-w-[140px] object-contain"
    />
  ),
};

// Country flags for GED solutions origin
const CountryFlags = {
  FR: () => (
    <svg viewBox="0 0 30 20" className="w-6 h-4 rounded shadow-sm">
      <rect width="10" height="20" fill="#002654"/>
      <rect x="10" width="10" height="20" fill="#FFFFFF"/>
      <rect x="20" width="10" height="20" fill="#CE1126"/>
    </svg>
  ),
  DE: () => (
    <svg viewBox="0 0 30 20" className="w-6 h-4 rounded shadow-sm">
      <rect width="30" height="6.67" fill="#000000"/>
      <rect y="6.67" width="30" height="6.67" fill="#DD0000"/>
      <rect y="13.33" width="30" height="6.67" fill="#FFCC00"/>
    </svg>
  ),
  ES: () => (
    <svg viewBox="0 0 30 20" className="w-6 h-4 rounded shadow-sm">
      <rect width="30" height="5" fill="#AA151B"/>
      <rect y="5" width="30" height="10" fill="#F1BF00"/>
      <rect y="15" width="30" height="5" fill="#AA151B"/>
    </svg>
  ),
  US: () => (
    <svg viewBox="0 0 30 20" className="w-6 h-4 rounded shadow-sm">
      <rect width="30" height="20" fill="#B22234"/>
      <rect y="1.54" width="30" height="1.54" fill="#FFFFFF"/>
      <rect y="4.62" width="30" height="1.54" fill="#FFFFFF"/>
      <rect y="7.69" width="30" height="1.54" fill="#FFFFFF"/>
      <rect y="10.77" width="30" height="1.54" fill="#FFFFFF"/>
      <rect y="13.85" width="30" height="1.54" fill="#FFFFFF"/>
      <rect y="16.92" width="30" height="1.54" fill="#FFFFFF"/>
      <rect width="12" height="10.77" fill="#3C3B6E"/>
    </svg>
  ),
  CA: () => (
    <svg viewBox="0 0 30 20" className="w-6 h-4 rounded shadow-sm">
      <rect width="7.5" height="20" fill="#FF0000"/>
      <rect x="7.5" width="15" height="20" fill="#FFFFFF"/>
      <rect x="22.5" width="7.5" height="20" fill="#FF0000"/>
      <path d="M15 4l-1 2.5-2.5-.5 1.5 2-2 1.5h2.5L13 12l2-1.5L17 12l-.5-2.5H19l-2-1.5 1.5-2-2.5.5z" fill="#FF0000"/>
    </svg>
  )
};

// GED solution country mapping
const solutionCountries = {
  zeendoc: { code: "FR", name: { fr: "France", es: "Francia", en: "France" } },
  docuware: { code: "DE", name: { fr: "Allemagne", es: "Alemania", en: "Germany" } },
  yooz: { code: "FR", name: { fr: "France", es: "Francia", en: "France" } },
  holded: { code: "ES", name: { fr: "Espagne", es: "España", en: "Spain" } },
  neoledge: { code: "FR", name: { fr: "France", es: "Francia", en: "France" } },
  twind: { code: "FR", name: { fr: "France", es: "Francia", en: "France" } },
  doxis: { code: "DE", name: { fr: "Allemagne", es: "Alemania", en: "Germany" } },
  opentext: { code: "CA", name: { fr: "Canada", es: "Canadá", en: "Canada" } },
  openkm: { code: "ES", name: { fr: "Espagne", es: "España", en: "Spain" } }
};

// "Ideal for" mapping per solution per language
const idealForMapping = {
  fr: {
    zeendoc: "PME/ETI, digitalisation factures",
    docuware: "Grandes entreprises, automatisation workflows",
    yooz: "Automatisation factures, cycle P2P",
    neoledge: "Grandes entreprises, gestion documentaire avancée, conformité",
    twind: "PME/ETI, GED collaborative moderne avec IA",
    doxis: "Entreprises, gestion documentaire intelligente, conformité",
    opentext: "Grands comptes, gestion d'information d'entreprise",
    openkm: "PME/ETI et secteur public, GED open-core hybride"
  },
  es: {
    docuware: "Grandes empresas, automatización workflows",
    yooz: "Automatización facturas, ciclo P2P",
    zeendoc: "PYME/ETI, digitalización de facturas",
    twind: "PYME/ETI, DMS colaborativa moderna con IA",
    doxis: "Empresas, gestión documental inteligente, cumplimiento",
    opentext: "Grandes empresas, gestión información empresarial",
    neoledge: "Grandes empresas, gestión documental avanzada, cumplimiento",
    openkm: "PYME/ETI y sector público, DMS open-core híbrida"
  },
  en: {
    docuware: "Large enterprises, workflow automation",
    yooz: "Invoice automation, P2P cycle",
    zeendoc: "SMEs / mid-market, invoice digitization",
    twind: "SMEs, modern collaborative DMS with AI",
    doxis: "Enterprises, intelligent document management, compliance",
    neoledge: "Large enterprises, advanced document management, compliance",
    opentext: "Large enterprises, enterprise information management",
    openkm: "SMEs and public sector, open-core hybrid DMS"
  }
};

const idealForLabel = {
  fr: "Idéal pour",
  es: "Ideal para",
  en: "Best for"
};

export const GEDSolutions = () => {
  const { language, t } = useLanguage();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        const response = await axios.get(`${API}/solutions/${language}`);
        setSolutions(response.data.solutions);
      } catch (error) {
        console.error("Error fetching solutions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSolutions();
  }, [language]);

  const demoLabels = {
    fr: "Demander une démo personnalisée",
    es: "Solicitar demo personalizada",
    en: "Request personalized demo"
  };

  const detailsLabels = {
    fr: { 
      features: "Fonctionnalités principales",
      sectors: "Secteurs d'activité",
      certs: "Certifications",
      deployment: "Déploiement",
      pricing: "Tarification",
      description: "Description",
      origin: "Pays d'origine"
    },
    es: {
      features: "Funcionalidades principales",
      sectors: "Sectores de actividad", 
      certs: "Certificaciones",
      deployment: "Despliegue",
      pricing: "Precios",
      description: "Descripción",
      origin: "País de origen"
    },
    en: {
      features: "Main features",
      sectors: "Business sectors",
      certs: "Certifications", 
      deployment: "Deployment",
      pricing: "Pricing",
      description: "Description",
      origin: "Country of origin"
    }
  };

  const labels = detailsLabels[language] || detailsLabels.fr;

  // State for demo request modal
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [selectedSolutionForDemo, setSelectedSolutionForDemo] = useState("");

  // External demo URL for Yooz partner
  const YOOZ_EXTERNAL_DEMO_URL = "https://www.getyooz.com/es/?utm_campaign=utm_partner_madebypartnertool&utm_source=bing&utm_medium=cpc&utm_term=yooz&utm_content=yooz";

  const handleDemoRequest = (solutionId, solutionName) => {
    // Yooz has a special external partner URL
    if (solutionId === "yooz") {
      window.open(YOOZ_EXTERNAL_DEMO_URL, "_blank", "noopener,noreferrer");
      return;
    }
    // All other solutions open the internal demo form
    setSelectedSolutionForDemo(solutionName);
    setDemoModalOpen(true);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3 mx-auto" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-slate-200 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ged-solutions" className="max-w-7xl mx-auto" data-testid="ged-solutions">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          {t.solutions.title}
        </h2>
        <p className="text-slate-600 text-lg">{t.solutions.subtitle}</p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {solutions.map((solution, index) => {
          const Logo = GEDLogos[solution.id];
          const countryInfo = solutionCountries[solution.id];
          const CountryFlag = countryInfo ? CountryFlags[countryInfo.code] : null;

          return (
            <motion.a
              key={solution.id}
              href={`/solution/${solution.id}`}
              onClick={() => trackPopupRedirect(solution.id)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden card-hover group cursor-pointer flex flex-col h-full no-underline"
              data-testid={`solution-card-${solution.id}`}
            >
              {/* Color Header with Logo */}
              <div className="p-4 flex items-center justify-between bg-white">
                <div className="h-12 flex items-center">
                  {Logo && <Logo />}
                </div>
                <ExternalLink
                  size={18}
                  className="text-slate-400 group-hover:text-slate-600 transition-colors"
                />
              </div>

              <div className="p-6 flex flex-col flex-grow">
                {/* Name with country */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-xl text-slate-900">
                    {solution.name}
                  </h3>
                  {countryInfo && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      {CountryFlag && <CountryFlag />}
                      {countryInfo.name[language]}
                    </span>
                  )}
                </div>

                {/* Ideal For */}
                {idealForMapping[language]?.[solution.id] && (
                  <p className="text-sm text-blue-600 font-medium mb-3">
                    <span className="text-slate-500">{idealForLabel[language]}:</span>{" "}
                    {idealForMapping[language][solution.id]}
                  </p>
                )}

                {/* Description */}
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                  {solution.description}
                </p>

                {/* Features */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {solution.features.slice(0, 3).map((feature, idx) => (
                      <Badge
                        key={idx}
                        variant="secondary"
                        className="text-xs"
                      >
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Footer - pushed to bottom */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-auto">
                  <div>
                    <p className="text-xs text-slate-500">{t.results.deployment}</p>
                    <p className="text-sm font-medium text-slate-700">
                      {solution.deployment.join(" / ")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{t.results.pricing}</p>
                    <p className="text-lg font-bold text-slate-900">
                      {solution.pricing}
                    </p>
                  </div>
                </div>

                {/* CTA hint */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-blue-600 font-semibold group-hover:underline">
                    {language === "fr" ? "Voir la fiche complète →" : language === "es" ? "Ver ficha completa →" : "View full page →"}
                  </span>
                </div>
              </div>
            </motion.a>
          );
        })}
      </div>

      {/* (Popups removed — every solution card now navigates directly to /solution/:id for SEO) */}

      {/* keeping legacy refs below for back-compat */}
      {false && (
        <Dialog>
          <DialogTrigger asChild><div /></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>_</DialogTitle></DialogHeader></DialogContent>
        </Dialog>
      )}

      {false && (() => {
        const _unused = [Calendar, Check, Sparkles];
        return null;
      })()}

      {/* Compare with other solutions CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mt-10"
      >
        <button
          onClick={() => {
            const analyzerEl = document.getElementById("analyzer");
            if (analyzerEl) analyzerEl.scrollIntoView({ behavior: "smooth" });
          }}
          className="text-blue-600 hover:text-blue-800 font-semibold text-sm underline underline-offset-4 transition-colors"
          data-testid="compare-others-cta"
        >
          {t.solutions.compareOthers}
        </button>
      </motion.div>
      
      {/* Demo Request Modal */}
      <DemoRequestModal 
        isOpen={demoModalOpen} 
        onClose={() => setDemoModalOpen(false)} 
        solutionName={selectedSolutionForDemo}
      />
    </div>
  );
};

export default GEDSolutions;